import type { ReactNode } from 'react';
import { Image } from '../../../../modules/design-system';
import { formatPrice, loadOperatorCurrency } from '../../../../modules/currency';

/** What a row shows of a card — a Vault item or a card inside a shipment. */
export type CardSummary = {
  title: string;
  imageUrl: string | null;
  fairMarketValue: number | null;
};

type Props = {
  item: CardSummary;
  /** Opens the card page; without it the row is a plain summary (the card on
   *  the Ship Card and shipment pages). */
  onOpen?: () => void;
  /** Extra line under the title — the Vault's auto-sell countdown. */
  footer?: ReactNode;
};

const THUMB_WIDTH = 60;
const THUMB_HEIGHT = 101;
const PRICE_DECIMALS = 2;

const ROW_CLASS =
  'flex w-full items-center gap-4 rounded-[18px] border border-solid p-4 text-left ' +
  'bg-[var(--co-MyPacksItemCard-bgFrom,#1A1B1F)] border-[var(--co-MyPacksItemCard-borderColor,rgba(255,255,255,0.01))]';

/**
 * One Vault card as a list row (partner Figma 2026-10-08): the slab, its fair
 * market value, the title and an optional footer line. The whole row opens
 * the card page.
 */
export function VaultCardRow({ item, onOpen, footer }: Props) {
  const fairValue =
    item.fairMarketValue != null && item.fairMarketValue > 0 && loadOperatorCurrency()
      ? formatPrice(item.fairMarketValue, PRICE_DECIMALS)
      : null;
  const content = (
    <>
      {/* The radius sits on a wrapper, as on the Shipping tab: Image adds its
          own `rounded-[inherit]`, which outranks a radius passed to it and
          would round the slab like the 18px row. */}
      <span className="relative block h-[101px] w-[60px] shrink-0 overflow-hidden rounded-[4.3px] [box-shadow:var(--co-MyPacksItemCard-photoShadow,0_4px_16px_2px_rgba(0,0,0,0.2))]">
        <Image
          src={item.imageUrl}
          alt=""
          className="absolute inset-0"
          imgClassName="absolute inset-0 h-full w-full object-cover"
          width={THUMB_WIDTH}
          height={THUMB_HEIGHT}
        />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex flex-col gap-1">
          {fairValue && (
            <span className="text-[16px] font-semibold leading-[20px] tracking-[-0.31px] text-[var(--co-MyPacksItemCard-valueColor,#FFFFFF)]">
              {fairValue}
            </span>
          )}
          <span className="line-clamp-2 text-[14px] leading-[20px] tracking-[-0.15px] text-[var(--co-MyPacksItemCard-titleColor,#6D707C)]">
            {item.title}
          </span>
        </span>
        {footer}
      </span>
    </>
  );

  if (!onOpen) {
    return (
      <div data-co-component="MyPacksItemCard" className={ROW_CLASS}>
        {content}
      </div>
    );
  }
  return (
    <button type="button" data-co-component="MyPacksItemCard" onClick={onOpen} className={`${ROW_CLASS} cursor-pointer transition-transform active:scale-[0.99]`}>
      {content}
    </button>
  );
}
